import { Send as SendIcon } from "@mui/icons-material";
import { Box, CircularProgress, Divider, Grid, IconButton, InputAdornment, TextField } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";

import { useWrappedChat } from "~hooks/useWrappedChat";
import { ChatMessageList } from "~ui/molecules/Chat/ChatMessageList";

interface ChatProps {
  eventId?: string;
  roomId?: string;
  dayOfEmergencyId?: string;
}

export const Chat = ({ eventId, roomId, dayOfEmergencyId }: ChatProps) => {
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  const { addMessage, isConnected, messages } = useWrappedChat({ dayOfEmergencyId, eventId, roomId });

  useLayoutEffect(() => {
    if (chatWindowRef?.current) {
      const scrollTop = chatWindowRef.current.scrollHeight - chatWindowRef.current.clientHeight;
      if (chatWindowRef.current.scrollTop !== scrollTop) {
        chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight - chatWindowRef.current.clientHeight;
      }
    }
  });

  const handleSend = async () => {
    if (!message.trim() || isSending) {
      return;
    }

    setIsSending(true);
    try {
      await addMessage(message);
      setMessage("");
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSend();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Chat Messages */}
      <Box
        ref={chatWindowRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
        }}
      >
        <ChatMessageList messages={messages} isConnected={isConnected} />
      </Box>
      <Divider />
      {/* Chat Input */}
      <Box sx={{ p: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} alignItems="center">
            <Grid size="grow">
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Type a message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                size="small"
                disabled={!isConnected || isSending}
                autoComplete="off"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                slotProps={{
                  htmlInput: {
                    autoCapitalize: "off",
                    autoComplete: "off",
                    autoCorrect: "off",
                    spellCheck: "false",
                  },

                  input: {
                    endAdornment: isSending && (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ),
                    sx: {
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "normal",
                      },
                      alignItems: "flex-start",
                      p: 1,
                    },
                  },
                }}
              />
            </Grid>
            <Grid>
              <IconButton
                onClick={handleSend}
                disabled={!isConnected || isSending || !message.trim()}
                color="primary"
                size="medium"
              >
                <SendIcon />
              </IconButton>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Box>
  );
};
